/**
 * RoutineSection — la sección más densa del plan.
 *
 * Render: por cada día de la semana ACTIVA una tabla de ejercicios con
 * GIF + nombre + sets/reps/peso/descanso/RIR. Todos los campos editables
 * inline. En modo editor, cada fila tiene:
 *   - Botón 📚 para reemplazar ejercicio desde catálogo
 *   - Botón ✕ para eliminar la fila
 * Al final de cada día, botón "+ Ejercicio" abre ExercisePickerModal.
 *
 * Multi-semana: arriba un tab strip — una tab por semana + "+ Semana".
 * Click en una tab cambia la semana visible (state local `activeWeekIndex`).
 * "+ Semana" duplica la semana actual con ids NUEVOS (genId) para que los
 * styles persistidos no se compartan entre semanas. Cada tab (salvo si es
 * la única) tiene un ✕ para eliminar esa semana. El label de la semana es
 * editable inline.
 *
 * En `readonly` (vista pública / PDF): se muestran TODAS las semanas, una
 * debajo de otra, cada una con su <h3>. Sin tabs (es para imprimir).
 */
import { useEffect, useRef, useState } from "react";
import { absUploadUrl } from "../../../api/client";
import { exerciseGifUrl, type Exercise } from "../../../api/exercises";
import { useCatalog } from "../../../contexts/CatalogContext";
import type { RoutineData, RoutineDay, RoutineExercise, RoutineWeek } from "../../../types/plan";
import { genId } from "../../../types/plan";
import { Icon } from "../../../lib/Icon";
import { InlineEditable } from "../InlineEditable";
import { ExercisePickerModal } from "../ExercisePickerModal";
import { ExerciseNameAutocomplete } from "./ExerciseNameAutocomplete";
import { PageHeader, SectionPage } from "./section-shared";

/**
 * Normaliza un nombre de ejercicio para matchearlo contra el catálogo.
 * Quita acentos, pasa a minúsculas, colapsa espacios.
 */
function normalize(s: string): string {
  return (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

interface RoutineSectionProps {
  data: RoutineData;
  onChange: (next: RoutineData) => void;
  readonly?: boolean;
  /** Botón "Generar por IA" inline. Solo se pasa desde el editor (PlanBuilder).
   *  PrintablePlanView y PublicPlanScreen lo dejan undefined → no se renderiza.
   *  Doble protección con `readonly` para ocultar en cualquier read-only context. */
  onGenerateWithAi?: () => void;
}

export function RoutineSection({ data, onChange, readonly, onGenerateWithAi }: RoutineSectionProps) {
  // pickerTarget = { weekIndex, dayIndex, exIndex? }. Si exIndex está, REEMPLAZA
  // esa fila. Si no, AGREGA al final del día.
  const [pickerTarget, setPickerTarget] = useState<{
    weekIndex: number;
    dayIndex: number;
    exIndex?: number;
  } | null>(null);
  const [activeWeekIndex, setActiveWeekIndex] = useState(0);
  const { exercises, loaded, ensureLoaded } = useCatalog();
  const autofilledRef = useRef<Set<string>>(new Set());

  const weeks = data.weeks || [];
  // Clamp del índice activo: si se eliminó la última semana, retroceder.
  const wi = Math.min(activeWeekIndex, Math.max(0, weeks.length - 1));
  useEffect(() => {
    if (activeWeekIndex !== wi) setActiveWeekIndex(wi);
  }, [wi, activeWeekIndex]);

  // Auto-rellenar GIFs de ejercicios sin gifUrl matcheando por nombre contra
  // el catálogo. Esto pasa típicamente cuando se carga una plantilla — los
  // ejercicios pre-llenos solo traen `name`. Cargamos el catálogo lazy y
  // hacemos un solo pase de relleno por sesión. Cubre TODAS las semanas.
  useEffect(() => {
    if (readonly) return;
    const needsFill = (data.weeks || []).some((w) =>
      w.days.some((d) =>
        d.exercises.some((e) => !e.gifUrl && e.name && !autofilledRef.current.has(normalize(e.name)))
      )
    );
    if (!needsFill) return;
    void ensureLoaded();
  }, [data, readonly, ensureLoaded]);

  useEffect(() => {
    if (!loaded || readonly) return;
    // Index del catálogo por id y por nombre normalizado para lookup O(1).
    const byId = new Map<string, Exercise>();
    const byName = new Map<string, Exercise>();
    for (const ex of exercises) {
      byId.set(String(ex.id), ex);
      const key = normalize(ex.name);
      if (!byName.has(key)) byName.set(key, ex);
      // También indexamos el nombre en español (a veces es campo separado)
      const nameEs = (ex as any).name_es;
      if (nameEs) {
        const k2 = normalize(nameEs);
        if (!byName.has(k2)) byName.set(k2, ex);
      }
    }

    let dirty = false;
    const next = cloneRoutine(data);
    for (const week of next.weeks || []) {
      for (const day of week.days) {
        for (const e of day.exercises) {
          if (e.gifUrl) continue;
          // 1) Si el ejercicio ya trae libId (ej. plan generado por IA), lo
          //    resolvemos por id — determinístico, no depende del nombre. Al
          //    venir por id tomamos también el nombre canónico del catálogo
          //    (corrige cualquier paráfrasis del modelo).
          let match: Exercise | undefined;
          let resolvedById = false;
          if (e.libId != null && e.libId !== "") {
            match = byId.get(String(e.libId));
            if (match) resolvedById = true;
          }
          // 2) Fallback: match por nombre normalizado exacto (plantillas,
          //    ejercicios tipeados a mano). Dedupe por nombre para no reintentar
          //    en cada render los que no matchean.
          if (!match) {
            const key = normalize(e.name);
            if (!key || autofilledRef.current.has(key)) continue;
            autofilledRef.current.add(key);
            match = byName.get(key);
          }
          if (match) {
            const url = exerciseGifUrl(match);
            if (url) {
              e.gifUrl = url;
              e.libId = match.id;
              if (resolvedById && match.name) e.name = match.name;
              e.muscle = e.muscle || match.muscle || "";
              e.equipment = e.equipment || match.equipment || "";
              dirty = true;
            }
          }
        }
      }
    }
    if (dirty) onChange(next);
    // intencionalmente excluimos onChange/data (no queremos loop infinito)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, exercises, readonly]);

  const updateExercise = (
    weekIndex: number,
    dayIndex: number,
    exIndex: number,
    patch: Partial<RoutineExercise>,
  ) => {
    const next = cloneRoutine(data);
    const day = next.weeks[weekIndex].days[dayIndex];
    day.exercises[exIndex] = { ...day.exercises[exIndex], ...patch };
    onChange(next);
  };

  const updateDay = (
    weekIndex: number,
    dayIndex: number,
    patch: Partial<{ label: string; focus: string }>,
  ) => {
    const next = cloneRoutine(data);
    const day = next.weeks[weekIndex].days[dayIndex];
    if (patch.label !== undefined) day.label = patch.label;
    if (patch.focus !== undefined) day.focus = patch.focus;
    onChange(next);
  };

  const removeExercise = (weekIndex: number, dayIndex: number, exIndex: number) => {
    const next = cloneRoutine(data);
    next.weeks[weekIndex].days[dayIndex].exercises.splice(exIndex, 1);
    onChange(next);
  };

  const moveExercise = (
    weekIndex: number,
    dayIndex: number,
    exIndex: number,
    dir: -1 | 1,
  ) => {
    const next = cloneRoutine(data);
    const arr = next.weeks[weekIndex].days[dayIndex].exercises;
    const target = exIndex + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[exIndex], arr[target]] = [arr[target], arr[exIndex]];
    onChange(next);
  };

  /** Ejercicio cuyo GIF se está previsualizando (modal grande con ejecución). */
  const [previewing, setPreviewing] = useState<RoutineExercise | null>(null);

  const updateWeekLabel = (weekIndex: number, label: string) => {
    const next = cloneRoutine(data);
    next.weeks[weekIndex].label = label;
    onChange(next);
  };

  const addWeek = () => {
    const next = cloneRoutine(data);
    const src = next.weeks[wi];
    // Duplicar la semana activa con ids NUEVOS en week/days/exercises para que
    // los styles persistidos (que se indexan por id) NO se compartan.
    const dup: RoutineWeek = src
      ? {
          id: genId("w"),
          label: `Semana ${next.weeks.length + 1}`,
          days: src.days.map((d) => ({
            ...d,
            id: genId("d"),
            exercises: d.exercises.map((e) => ({ ...e, id: genId("ex") })),
          })),
        }
      : { id: genId("w"), label: "Semana 1", days: [] };
    next.weeks.push(dup);
    onChange(next);
    setActiveWeekIndex(next.weeks.length - 1);
  };

  const removeWeek = (weekIndex: number) => {
    if (weeks.length <= 1) return;
    if (!confirm(`¿Eliminar "${weeks[weekIndex]?.label || `Semana ${weekIndex + 1}`}"?`)) return;
    const next = cloneRoutine(data);
    next.weeks.splice(weekIndex, 1);
    onChange(next);
    setActiveWeekIndex((cur) => Math.min(cur, next.weeks.length - 1));
  };

  const handlePick = (ex: Exercise) => {
    if (!pickerTarget) return;
    const { weekIndex, dayIndex, exIndex } = pickerTarget;
    const next = cloneRoutine(data);
    const day = next.weeks[weekIndex].days[dayIndex];
    const newRow: RoutineExercise = {
      id: genId("ex"),
      libId: ex.id,
      name: ex.name,
      sets: 3,
      reps: "10-12",
      weight: "",
      rest: "60s",
      rir: 2,
      notes: ex.description || "",
      gifUrl: exerciseGifUrl(ex) || "",
      muscle: ex.muscle || ex.primary_muscle || "",
      equipment: ex.equipment || "",
    };
    if (exIndex !== undefined) {
      // Reemplazo: preservamos sets/reps/weight si el user los había modificado
      const cur = day.exercises[exIndex];
      day.exercises[exIndex] = {
        ...newRow,
        id: cur?.id || newRow.id,
        sets: cur?.sets ?? newRow.sets,
        reps: cur?.reps ?? newRow.reps,
        weight: cur?.weight ?? newRow.weight,
        rest: cur?.rest ?? newRow.rest,
        rir: cur?.rir ?? newRow.rir,
      };
    } else {
      day.exercises.push(newRow);
    }
    onChange(next);
    setPickerTarget(null);
  };

  return (
    <SectionPage readonly={readonly}>
      <PageHeader
        eyebrow="Rutina de entrenamiento"
        title="Rutina de entrenamiento"
        sub={readonly ? undefined : weeks[wi]?.label}
      />

      {readonly ? (
        // Vista pública / PDF: todas las semanas, una debajo de otra.
        weeks.map((week, weekIndex) => (
          <div key={week.id || weekIndex} className="al-week-print">
            <h3 className="al-week-print-title">{week.label || `Semana ${weekIndex + 1}`}</h3>
            <WeekDays
              week={week}
              weekIndex={weekIndex}
              readonly
              updateDay={updateDay}
              updateExercise={updateExercise}
              removeExercise={removeExercise}
              moveExercise={moveExercise}
              onPick={setPickerTarget}
              onPreview={setPreviewing}
            />
          </div>
        ))
      ) : (
        <>
          {/* Strip "Generar con IA" — solo visible para el coach en el editor.
              No se renderiza en PrintablePlanView/PublicPlanScreen porque ahí
              `onGenerateWithAi` no se pasa. La clase `al-pb-coach-only` hace
              el ocultamiento via @media print por si en el futuro queremos
              imprimir directo desde el editor sin pasar por PrintablePlanView. */}
          {onGenerateWithAi && !readonly && (
            <div className="al-section-ai-strip al-pb-coach-only">
              <button type="button" className="al-section-ai-btn" onClick={onGenerateWithAi}>
                <Icon name="sparkle" size={12} /> Generar rutina con IA
              </button>
            </div>
          )}

          {/* Tab strip de semanas */}
          <div className="al-week-tabs" role="tablist">
            {weeks.map((week, weekIndex) => (
              <div
                key={week.id || weekIndex}
                className={"al-week-tab " + (weekIndex === wi ? "is-active" : "")}
                role="tab"
                aria-selected={weekIndex === wi}
                onClick={() => setActiveWeekIndex(weekIndex)}
              >
                {weekIndex === wi ? (
                  <InlineEditable
                    value={week.label}
                    onChange={(v) => updateWeekLabel(weekIndex, v)}
                    placeholder={`Semana ${weekIndex + 1}`}
                    fieldPath={`week.${week.id || weekIndex}.label`}
                  />
                ) : (
                  <span className="al-week-tab-lbl">{week.label || `Semana ${weekIndex + 1}`}</span>
                )}
                {weeks.length > 1 && (
                  <button
                    type="button"
                    className="al-week-tab-del"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeWeek(weekIndex);
                    }}
                    title="Eliminar semana"
                  >
                    <Icon name="x" size={10} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="al-week-tab-add" onClick={addWeek}>
              <Icon name="plus" size={11} /> Semana
            </button>
          </div>

          {weeks[wi] && (
            <WeekDays
              week={weeks[wi]}
              weekIndex={wi}
              updateDay={updateDay}
              updateExercise={updateExercise}
              removeExercise={removeExercise}
              moveExercise={moveExercise}
              onPick={setPickerTarget}
              onPreview={setPreviewing}
            />
          )}
        </>
      )}

      {/* Resumen: la semana de entrenamiento en tabla (días en columnas) —
          versión compacta para imprimir. Una tabla por semana. */}
      {weeks.map((week, i) => (
        <RoutineWeekTable key={week.id || i} week={week} />
      ))}

      <ExercisePickerModal
        open={!!pickerTarget}
        onClose={() => setPickerTarget(null)}
        onPick={handlePick}
      />

      {previewing && (
        <ExercisePreviewModal
          exercise={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </SectionPage>
  );
}

// ----------------------------------------------------------------------
// RoutineWeekTable — resumen semanal: días en columnas, ejercicios en la celda
// ----------------------------------------------------------------------

function RoutineWeekTable({ week }: { week: RoutineWeek }) {
  const days = week.days || [];
  if (days.length === 0) return null;
  return (
    <div className="al-routine-wk">
      <div className="al-routine-wk-title">
        Resumen semanal · {week.label}
        <span className="tiny muted"> · para imprimir</span>
      </div>
      <div className="al-routine-wk-scroll">
        <table className="al-pg-table al-routine-wk-table">
          <thead>
            <tr>
              {days.map((d, i) => (
                <th key={d.id || i}>
                  <div className="al-routine-wk-day">{d.label}</div>
                  {d.focus ? <div className="al-routine-wk-focus">{d.focus}</div> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {days.map((d, i) => (
                <td key={d.id || i}>
                  {d.exercises.length > 0 ? (
                    <ol className="al-routine-wk-list">
                      {d.exercises.map((e, ei) => (
                        <li key={e.id || ei}>
                          {e.libId ? (
                            <a
                              className="al-routine-wk-name al-ex-name-link"
                              href={`/exercise/${e.libId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Ver: ${e.name}`}
                            >
                              {e.name}
                            </a>
                          ) : (
                            <span className="al-routine-wk-name">{e.name}</span>
                          )}
                          <span className="al-routine-wk-sr">
                            {e.sets}×{e.reps}
                          </span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <span className="al-routine-wk-rest">
                      {d.label.toLowerCase().includes("desc") ? "Descanso" : "—"}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface WeekDaysProps {
  week: RoutineWeek;
  weekIndex: number;
  readonly?: boolean;
  updateDay: (
    weekIndex: number,
    dayIndex: number,
    patch: Partial<{ label: string; focus: string }>,
  ) => void;
  updateExercise: (
    weekIndex: number,
    dayIndex: number,
    exIndex: number,
    patch: Partial<RoutineExercise>,
  ) => void;
  removeExercise: (weekIndex: number, dayIndex: number, exIndex: number) => void;
  moveExercise: (weekIndex: number, dayIndex: number, exIndex: number, dir: -1 | 1) => void;
  onPick: (t: { weekIndex: number; dayIndex: number; exIndex?: number }) => void;
  onPreview: (ex: RoutineExercise) => void;
}

/** Render de los días de UNA semana — reutilizado en editor y readonly. */
function WeekDays({
  week,
  weekIndex,
  readonly,
  updateDay,
  updateExercise,
  removeExercise,
  moveExercise,
  onPick,
  onPreview,
}: WeekDaysProps) {
  return (
    <div className="al-routine">
      {week.days.map((day: RoutineDay, di) => {
        const did = day.id || `idx${di}`;
        return (
          <div className="al-day" id={day.id ? `day-${day.id}` : undefined} key={day.id || di}>
            <div className="al-day-hd">
              <div className="al-day-lbl">
                <InlineEditable
                  value={day.label}
                  onChange={(v) => updateDay(weekIndex, di, { label: v })}
                  readonly={readonly}
                  fieldPath={`day.${did}.label`}
                />
              </div>
              <div className="al-day-focus">
                <InlineEditable
                  value={day.focus || ""}
                  onChange={(v) => updateDay(weekIndex, di, { focus: v })}
                  readonly={readonly}
                  placeholder="Foco del día (Pecho · Tríceps…)"
                  fieldPath={`day.${did}.focus`}
                />
              </div>
            </div>

            {day.exercises.length > 0 ? (
              <table className="al-pg-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Ejercicio</th>
                    <th>Series</th>
                    <th>Reps</th>
                    <th>Peso</th>
                    <th>Descanso</th>
                    <th>RIR</th>
                    {!readonly && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {day.exercises.map((e, ei) => {
                    const eid = e.id || `idx${ei}`;
                    return (
                      <tr key={e.id || ei}>
                        <td>
                          {readonly ? (
                            // En vista pública: si el ejercicio viene del catálogo
                            // (libId), el GIF es CLICKEABLE → abre /exercise/{id}
                            // en pestaña nueva igual que el link del nombre. Sin
                            // libId (ejercicio custom del coach) se renderiza
                            // como div estático sin link.
                            e.libId ? (
                              <a
                                className="al-ex-gif al-ex-gif-link"
                                href={`/exercise/${e.libId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Ver ${e.name}`}
                                aria-label={`Abrir detalle de ${e.name}`}
                              >
                                {e.gifUrl ? (
                                  <img src={absUploadUrl(e.gifUrl)} alt="" loading="lazy" />
                                ) : (
                                  <div className="al-ex-gif-fb">
                                    {(e.name || "")
                                      .split(" ")
                                      .slice(0, 2)
                                      .map((w) => w[0] || "")
                                      .join("")
                                      .toUpperCase()}
                                  </div>
                                )}
                              </a>
                            ) : (
                              <div className="al-ex-gif">
                                {e.gifUrl ? (
                                  <img src={absUploadUrl(e.gifUrl)} alt="" loading="lazy" />
                                ) : (
                                  <div className="al-ex-gif-fb">
                                    {(e.name || "")
                                      .split(" ")
                                      .slice(0, 2)
                                      .map((w) => w[0] || "")
                                      .join("")
                                      .toUpperCase()}
                                  </div>
                                )}
                              </div>
                            )
                          ) : (
                            <button
                              type="button"
                              className="al-ex-gif al-ex-gif-btn"
                              onClick={() => onPreview(e)}
                              title="Ver GIF grande + ejecución"
                            >
                              {e.gifUrl ? (
                                <img src={absUploadUrl(e.gifUrl)} alt="" loading="lazy" />
                              ) : (
                                <div className="al-ex-gif-fb">
                                  {(e.name || "")
                                    .split(" ")
                                    .slice(0, 2)
                                    .map((w) => w[0] || "")
                                    .join("")
                                    .toUpperCase()}
                                </div>
                              )}
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="al-ex-name-row">
                            <b>
                              {readonly ? (
                                e.libId ? (
                                  <a
                                    className="al-ex-name-link"
                                    href={`/exercise/${e.libId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Ver cómo se hace: ${e.name}`}
                                  >
                                    <InlineEditable
                                      value={e.name}
                                      onChange={(v) => updateExercise(weekIndex, di, ei, { name: v })}
                                      readonly
                                      fieldPath={`day.${did}.ex.${eid}.name`}
                                    />
                                  </a>
                                ) : (
                                  <InlineEditable
                                    value={e.name}
                                    onChange={(v) => updateExercise(weekIndex, di, ei, { name: v })}
                                    readonly
                                    fieldPath={`day.${did}.ex.${eid}.name`}
                                  />
                                )
                              ) : (
                                <ExerciseNameAutocomplete
                                  value={e.name}
                                  onCommitText={(v) => updateExercise(weekIndex, di, ei, { name: v })}
                                  onPick={(ex) => updateExercise(weekIndex, di, ei, {
                                    name: ex.name,
                                    libId: ex.id,
                                    gifUrl: exerciseGifUrl(ex) || "",
                                    muscle: ex.muscle || ex.primary_muscle || "",
                                    equipment: ex.equipment || "",
                                  })}
                                  placeholder="Nombre del ejercicio"
                                />
                              )}
                            </b>
                            {!readonly && (
                              <button
                                type="button"
                                className="al-ex-pick-btn"
                                onClick={() => onPick({ weekIndex, dayIndex: di, exIndex: ei })}
                                title="Abrir catálogo completo (filtros + GIFs)"
                              >
                                <Icon name="book" size={12} />
                              </button>
                            )}
                          </div>
                          {(e.notes || !readonly) && (
                            <div className="al-pg-table-note">
                              <InlineEditable
                                value={e.notes || ""}
                                onChange={(v) => updateExercise(weekIndex, di, ei, { notes: v })}
                                readonly={readonly}
                                placeholder="Notas / técnica…"
                                fieldPath={`day.${did}.ex.${eid}.notes`}
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          <InlineEditable
                            value={String(e.sets ?? "")}
                            onChange={(v) => updateExercise(weekIndex, di, ei, { sets: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.ex.${eid}.sets`}
                          />
                        </td>
                        <td>
                          <InlineEditable
                            value={e.reps}
                            onChange={(v) => updateExercise(weekIndex, di, ei, { reps: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.ex.${eid}.reps`}
                          />
                        </td>
                        <td>
                          <InlineEditable
                            value={e.weight}
                            onChange={(v) => updateExercise(weekIndex, di, ei, { weight: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.ex.${eid}.weight`}
                          />
                        </td>
                        <td>
                          <InlineEditable
                            value={e.rest}
                            onChange={(v) => updateExercise(weekIndex, di, ei, { rest: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.ex.${eid}.rest`}
                          />
                        </td>
                        <td>
                          <InlineEditable
                            value={String(e.rir ?? "")}
                            onChange={(v) => updateExercise(weekIndex, di, ei, { rir: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.ex.${eid}.rir`}
                          />
                        </td>
                        {!readonly && (
                          <td>
                            <div className="al-ex-row-actions">
                              <button
                                type="button"
                                className="al-ex-move-btn"
                                onClick={() => moveExercise(weekIndex, di, ei, -1)}
                                disabled={ei === 0}
                                title="Subir ejercicio"
                                aria-label="Subir"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="al-ex-move-btn"
                                onClick={() => moveExercise(weekIndex, di, ei, 1)}
                                disabled={ei === day.exercises.length - 1}
                                title="Bajar ejercicio"
                                aria-label="Bajar"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className="al-ex-del-btn"
                                onClick={() => removeExercise(weekIndex, di, ei)}
                                title="Eliminar ejercicio"
                              >
                                <Icon name="x" size={12} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="al-day-rest">
                — {day.label.toLowerCase().includes("desc") ? "Día de descanso" : "Sin ejercicios programados"}
              </div>
            )}

            {!readonly && (
              <button
                type="button"
                className="al-day-add-ex"
                onClick={() => onPick({ weekIndex, dayIndex: di })}
              >
                <Icon name="plus" size={12} /> Agregar ejercicio
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function cloneRoutine(d: RoutineData): RoutineData {
  return {
    weeks: (d.weeks || []).map((w) => ({
      ...w,
      days: w.days.map((day) => ({
        ...day,
        exercises: day.exercises.map((ex) => ({ ...ex })),
      })),
    })),
  };
}

// ----------------------------------------------------------------------
// ExercisePreviewModal — al clickear el GIF en el editor, se abre este
// modal con el GIF grande + músculos + "Cómo hacerlo" del catálogo. Permite
// al coach VERIFICAR que eligió el ejercicio correcto antes de seguir
// armando la rutina. Pull de instructions desde el catálogo vía libId.
// ----------------------------------------------------------------------

function ExercisePreviewModal({
  exercise,
  onClose,
}: {
  exercise: RoutineExercise;
  onClose: () => void;
}) {
  const { exercises, ensureLoaded } = useCatalog();
  useEffect(() => {
    if (exercise.libId) void ensureLoaded();
  }, [exercise.libId, ensureLoaded]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const catalogEx = exercise.libId
    ? exercises.find((c) => String(c.id) === String(exercise.libId))
    : null;

  const name = catalogEx?.name || exercise.name;
  const gif = catalogEx ? exerciseGifUrl(catalogEx) : exercise.gifUrl;
  const muscle = catalogEx?.muscle || exercise.muscle;
  const equipment = catalogEx?.equipment || exercise.equipment;
  const instructions =
    catalogEx?.instructions || catalogEx?.description || exercise.notes || "";
  const secondary = catalogEx?.secondary_muscles;

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-ex-preview"
        role="dialog"
        aria-label="Detalle del ejercicio"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              {name}
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {muscle || "—"}
              {equipment ? ` · ${equipment}` : ""}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="al-ex-preview-body">
          <div className="al-ex-preview-hero">
            {gif ? (
              <img src={gif} alt={name} />
            ) : (
              <div className="al-ex-preview-hero-empty">
                <Icon name="hammer" size={36} />
                <span className="tiny" style={{ marginTop: 6 }}>Sin GIF disponible</span>
              </div>
            )}
          </div>
          <div className="al-ex-preview-info">
            {secondary && (
              <div className="al-ex-preview-tags">
                <span className="tiny muted">Músculos secundarios:</span>{" "}
                <b>{secondary}</b>
              </div>
            )}
            <div className="al-ex-preview-section-title">CÓMO HACERLO</div>
            {instructions ? (
              <p className="al-ex-preview-instructions">{instructions}</p>
            ) : (
              <p className="muted" style={{ fontStyle: "italic" }}>
                Sin instrucciones cargadas para este ejercicio. Podés agregarlas desde el
                admin de ejercicios.
              </p>
            )}
            <div className="al-ex-preview-actions">
              <button className="btn btn-secondary" onClick={onClose} type="button">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
